import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  User, Calendar, Clock, Bell, FileText, DollarSign, 
  MapPin, LogOut, CheckCircle, XCircle, AlertCircle,
  Briefcase, BookOpen, RefreshCw, Upload, GraduationCap, MessageSquare, Shield, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';
import { ROLE_MODULES, MODULE_MENU_MAPPING } from '@/hooks/useModuleAccess';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StaffDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [markingAttendance, setMarkingAttendance] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [adminRoles, setAdminRoles] = useState([]);
  const [adminModules, setAdminModules] = useState([]);
  const [openingAdmin, setOpeningAdmin] = useState(false);

  const staffName = localStorage.getItem('staffName') || 'Staff';
  const staffRole = localStorage.getItem('staffRole') || 'Staff';

  useEffect(() => {
    fetchDashboard();
    getLocation();
    detectAdminRoles();
  }, []);

  // Staff with assigned admin roles (e.g. Exam Controller) get an Admin Panel section
  const detectAdminRoles = () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) return;
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles = payload.roles || [];
      const selfServiceModules = ['dashboard', 'profile', 'staff-portal', 'lms'];
      const modules = new Set();
      const rolesWithAccess = [];
      roles.forEach(role => {
        const roleModules = (ROLE_MODULES[role] || []).filter(m => !selfServiceModules.includes(m));
        if (roleModules.length > 0 || role === 'Admin') {
          rolesWithAccess.push(role);
          roleModules.forEach(m => modules.add(m));
        }
      });
      setAdminRoles(rolesWithAccess);
      setAdminModules(Array.from(modules));
    } catch (e) { console.error('Role detection error:', e); }
  };

  const openAdminPanel = async () => {
    setOpeningAdmin(true);
    try {
      const token = localStorage.getItem('staffToken');
      const response = await fetch(`${API_URL}/api/staff-portal/exchange-token`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.access_token);
        localStorage.removeItem('collegeInfo');
        window.location.href = '/dashboard';
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Could not open admin panel');
      }
    } catch (e) {
      toast.error('Could not open admin panel');
    } finally {
      setOpeningAdmin(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        navigate('/staff-portal');
        return;
      }

      const response = await fetch(`${API_URL}/api/staff-portal/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      } else if (response.status === 401) {
        localStorage.removeItem('staffToken');
        navigate('/staff-portal');
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: Math.round(position.coords.accuracy || 0)
          });
          setLocationError('');
        },
        (error) => {
          setLocationError('Please enable location access to mark attendance');
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    } else {
      setLocationError('Geolocation is not supported by your browser');
    }
  };

  const markAttendance = async (type) => {
    if (!location) {
      toast.error('Please enable location access');
      getLocation();
      return;
    }

    setMarkingAttendance(true);
    try {
      const token = localStorage.getItem('staffToken');
      const response = await fetch(`${API_URL}/api/staff-portal/attendance/mark`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          latitude: location.latitude,
          longitude: location.longitude,
          type: type
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        if (!data.within_range) {
          toast.warning(`You are ${data.distance}m away from campus (allowed: ${data.allowed_radius}m)`);
        }
        fetchDashboard();
      } else {
        toast.error(data.detail || 'Failed to mark attendance');
      }
    } catch (error) {
      console.error('Attendance error:', error);
      toast.error('Failed to mark attendance');
    } finally {
      setMarkingAttendance(false);
    }
  };

  const handleLogout = () => {
    const tenantCode = localStorage.getItem('staff_tenant_code') || localStorage.getItem('tenant_code');
    localStorage.removeItem('staffToken');
    localStorage.removeItem('staffEmployeeId');
    localStorage.removeItem('staffName');
    localStorage.removeItem('staffRole');
    localStorage.removeItem('staff_tenant_code');
    // Stay on tenant-specific staff login page
    if (tenantCode) {
      window.location.href = `/${tenantCode}/staff-portal`;
    } else {
      navigate('/staff-portal');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const staff = dashboard?.staff || {};
  const attendance = dashboard?.attendance_summary || {};
  const todayAttendance = dashboard?.today_attendance || {};

  // Get profile photo from various possible fields
  const profilePhoto = staff.photo_url || staff.photo || staff.profile_photo;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-indigo-700 text-white shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
                {profilePhoto ? (
                  <img 
                    src={profilePhoto} 
                    alt={staff.full_name || 'Profile'} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="h-5 w-5 sm:h-6 sm:w-6" />
                )}
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold">Staff Portal</h1>
                <p className="text-indigo-200 text-xs sm:text-sm">{staff.employee_id}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 sm:gap-4">
              <Link to="/staff-portal/notifications" className="relative p-2 hover:bg-indigo-600 rounded-full">
                <Bell className="h-5 w-5 sm:h-6 sm:w-6 cursor-pointer" />
                {dashboard?.notifications_unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 bg-red-500 text-xs w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center text-[10px] sm:text-xs">
                    {dashboard.notifications_unread}
                  </span>
                )}
              </Link>
              <button onClick={handleLogout} className="flex items-center gap-2 hover:bg-indigo-600 p-2 rounded-lg">
                <LogOut className="h-5 w-5" />
                <span className="hidden sm:inline text-sm">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Welcome Card */}
        <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-0">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">Welcome, {staff.full_name || staffName}!</h2>
                <p className="text-indigo-200 mt-1 text-sm sm:text-base">
                  {staff.designation || staffRole} • {staff.department || 'Department'}
                </p>
              </div>
              <div className="sm:text-right">
                <p className="text-indigo-200 text-sm">Today</p>
                <p className="text-lg sm:text-xl font-semibold">{formatDate()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Attendance Mark Section */}
        <Card className="border-2 border-indigo-200">
          <CardHeader className="pb-3 bg-indigo-50">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-indigo-600" />
              Mark Attendance
              {location && (
                <span className={`text-xs px-2 py-0.5 rounded ${location.accuracy > 100 ? 'text-orange-700 bg-orange-100' : 'text-green-600 bg-green-100'}`} data-testid="location-status-badge">
                  {location.accuracy > 100 ? `GPS weak (±${location.accuracy}m)` : `Location Enabled${location.accuracy ? ` (±${location.accuracy}m)` : ''}`}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {locationError && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                {locationError}
                <Button size="sm" variant="outline" onClick={getLocation} className="ml-auto">
                  Enable Location
                </Button>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-gray-500">Today's Status</p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {todayAttendance.marked ? (
                    <>
                      {todayAttendance.status === 'Present' && <CheckCircle className="h-5 w-5 text-green-500" />}
                      {todayAttendance.status === 'Late' && <AlertCircle className="h-5 w-5 text-yellow-500" />}
                      {todayAttendance.status === 'Absent' && <XCircle className="h-5 w-5 text-red-500" />}
                      <span className={`font-semibold ${
                        todayAttendance.status === 'Present' ? 'text-green-600' :
                        todayAttendance.status === 'Late' ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {todayAttendance.status}
                      </span>
                      {todayAttendance.check_in && (
                        <span className="text-gray-500 text-xs sm:text-sm">
                          (In: {new Date(todayAttendance.check_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })})
                        </span>
                      )}
                      {todayAttendance.check_out && (
                        <span className="text-gray-500 text-xs sm:text-sm">
                          (Out: {new Date(todayAttendance.check_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })})
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-gray-500">Not marked yet</span>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3 w-full sm:w-auto">
                <Button
                  onClick={() => markAttendance('check_in')}
                  disabled={markingAttendance || todayAttendance.check_in || !location}
                  className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
                  data-testid="check-in-btn"
                >
                  {markingAttendance ? 'Marking...' : 'Check In'}
                </Button>
                <Button
                  onClick={() => markAttendance('check_out')}
                  disabled={markingAttendance || !todayAttendance.check_in || todayAttendance.check_out || !location}
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 flex-1 sm:flex-none"
                  data-testid="check-out-btn"
                >
                  {markingAttendance ? 'Marking...' : 'Check Out'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-2 sm:gap-4">
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-2 sm:p-4 text-center">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-1 sm:mb-2">
                <CheckCircle className="h-4 w-4 sm:h-6 sm:w-6 text-green-600" />
              </div>
              <p className="text-lg sm:text-2xl font-bold text-green-600">{attendance.present || 0}</p>
              <p className="text-xs sm:text-sm text-gray-500">Present</p>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-2 sm:p-4 text-center">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-1 sm:mb-2">
                <Clock className="h-4 w-4 sm:h-6 sm:w-6 text-yellow-600" />
              </div>
              <p className="text-lg sm:text-2xl font-bold text-yellow-600">{attendance.late || 0}</p>
              <p className="text-xs sm:text-sm text-gray-500">Late</p>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-2 sm:p-4 text-center">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-1 sm:mb-2">
                <XCircle className="h-4 w-4 sm:h-6 sm:w-6 text-red-600" />
              </div>
              <p className="text-lg sm:text-2xl font-bold text-red-600">{attendance.absent || 0}</p>
              <p className="text-xs sm:text-sm text-gray-500">Absent</p>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-2 sm:p-4 text-center">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-1 sm:mb-2">
                <Bell className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600" />
              </div>
              <p className="text-lg sm:text-2xl font-bold text-blue-600">{dashboard?.notifications_unread || 0}</p>
              <p className="text-xs sm:text-sm text-gray-500">Alerts</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Access */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base sm:text-lg">Quick Access</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-4">
              <Link to="/staff-portal/attendance" className="p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-indigo-50 transition-colors text-center group">
                <Calendar className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-1 sm:mb-2 text-indigo-600 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-gray-700 text-xs sm:text-sm">Attendance</p>
              </Link>
              
              <Link to="/staff-portal/salary" className="p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors text-center group">
                <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-1 sm:mb-2 text-green-600 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-gray-700 text-xs sm:text-sm">Salary</p>
              </Link>
              
              <Link to="/staff-portal/leave" className="p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-yellow-50 transition-colors text-center group">
                <FileText className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-1 sm:mb-2 text-yellow-600 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-gray-700 text-xs sm:text-sm">Leave</p>
              </Link>
              
              <Link to="/staff-portal/profile" className="p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-purple-50 transition-colors text-center group">
                <User className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-1 sm:mb-2 text-purple-600 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-gray-700 text-xs sm:text-sm">Profile</p>
              </Link>

              <Link to="/staff-portal/notifications" className="p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors text-center group relative">
                <Bell className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-1 sm:mb-2 text-blue-600 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-gray-700 text-xs sm:text-sm">Alerts</p>
                {(dashboard?.notifications_unread || 0) > 0 && (
                  <span className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-red-500 text-white text-[10px] sm:text-xs w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center">
                    {dashboard?.notifications_unread}
                  </span>
                )}
              </Link>

              <Link to="/staff-portal/messages" className="p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors text-center group relative">
                <MessageSquare className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-1 sm:mb-2 text-green-600 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-gray-700 text-xs sm:text-sm">Messages</p>
              </Link>

              <Link to="/staff-portal/regularization" className="p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-orange-50 transition-colors text-center group">
                <RefreshCw className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-1 sm:mb-2 text-orange-600 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-gray-700 text-xs sm:text-sm">Regularize</p>
              </Link>

              {/* My Classes - for all teaching staff */}
              <Link to="/staff-portal/my-classes" className="p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-cyan-50 transition-colors text-center group">
                <BookOpen className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-1 sm:mb-2 text-cyan-600 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-gray-700 text-xs sm:text-sm">Classes</p>
              </Link>

              {/* Exam Attendance - for teachers */}
              <Link to="/staff-portal/exam-attendance" className="p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-purple-50 transition-colors text-center group">
                <FileText className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-1 sm:mb-2 text-purple-600 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-gray-700 text-xs sm:text-sm">Exam Attend.</p>
              </Link>

              {/* Documents Upload */}
              <Link to="/staff-portal/documents" className="p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-pink-50 transition-colors text-center group">
                <Upload className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-1 sm:mb-2 text-pink-600 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-gray-700 text-xs sm:text-sm">Documents</p>
              </Link>

              {/* LMS - Learning Management System */}
              <Link to="/staff-portal/lms" className="p-3 sm:p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg hover:from-indigo-100 hover:to-purple-100 transition-colors text-center group border-2 border-indigo-200">
                <GraduationCap className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-1 sm:mb-2 text-indigo-600 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-indigo-700 text-xs sm:text-sm">LMS</p>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Admin Panel Access - shown when staff has assigned admin roles */}
        {adminModules.length > 0 && (
          <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid="admin-access-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-indigo-600" />
                Admin Panel Access
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-3">
                {adminRoles.map(role => (
                  <span key={role} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full font-medium">{role}</span>
                ))}
              </div>
              <p className="text-xs sm:text-sm text-gray-600 mb-3">
                You have access to: {adminModules.map(m => (MODULE_MENU_MAPPING[m] || [m])[0]).join(', ')}
              </p>
              <Button data-testid="open-admin-panel-btn" onClick={openAdminPanel} disabled={openingAdmin}
                className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <ExternalLink className="h-4 w-4 mr-2" />
                {openingAdmin ? 'Opening...' : 'Open Admin Panel'}
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
